import { EMPLOYEE_FUNCTION_LABELS } from "@etudescarre/constants";
import { isNil, isNotNil } from "@etudescarre/utils";
import {
  ArrowsClockwiseIcon,
  DownloadSimpleIcon,
  GearSixIcon,
  HardHatIcon,
  LightningIcon,
  ListIcon,
  SignOutIcon,
} from "@phosphor-icons/react";
import { useState } from "react";
import { useParams } from "react-router";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { useGuardedNavigate } from "@/lib/utils/unsaved-changes";
import { useYardContext } from "@/providers/yard-context";

type YardHeaderProps = {
  onToggleNav: () => void;
  onToggleActions: () => void;
};

export function YardHeader({ onToggleNav, onToggleActions }: YardHeaderProps) {
  const { yard, employee, primaryContract } = useYardContext();
  const { yardId } = useParams<{ yardId: string }>();
  const navigate = useGuardedNavigate();
  const [isDownloading, setIsDownloading] = useState(false);

  // Generate and download the "nomenclature générale" CSV export.
  // The dedicated worksite-selection screen is still a placeholder, so the entry
  // point lives here on the per-yard header for now.
  const handleDownloadNomenclature = async () => {
    if (isNil(yardId)) return;
    setIsDownloading(true);
    try {
      const { data, error } = await apiClient.GET(
        "/api/yards/{yardId}/downloads/{type}",
        {
          params: { path: { yardId, type: "nomenclature-generale" } },
          parseAs: "blob",
        },
      );

      if (error || isNil(data)) {
        toaster.error({
          title: "Erreur",
          description: "Échec du téléchargement",
        });
        return;
      }

      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `nomenclature-${yardId}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toaster.error({
        title: "Erreur",
        description: "Une erreur est survenue",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const employeeName = [employee.firstName, employee.lastName]
    .filter(isNotNil)
    .join(" ");

  const functionLabel = isNotNil(primaryContract?.function)
    ? (EMPLOYEE_FUNCTION_LABELS[primaryContract.function] ??
      primaryContract.function)
    : null;

  return (
    <header className="flex items-center justify-between border-b border-zinc-200 bg-white px-4 py-3 dark:border-zinc-800 dark:bg-zinc-900">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onToggleNav}
          className="cursor-pointer rounded-md p-2 text-zinc-600 hover:bg-zinc-100 lg:hidden dark:text-zinc-400 dark:hover:bg-zinc-800"
          aria-label="Ouvrir le menu de navigation"
          title="Menu de navigation"
        >
          <ListIcon className="size-5" />
        </button>

        <div className="flex items-center gap-2">
          <HardHatIcon className="size-5 text-zinc-500" />
          <div>
            <div className="flex items-center gap-1">
              <h1 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                Bienvenue sur {yard.name}
              </h1>
              <button
                type="button"
                onClick={() => navigate(`/yards/${yardId}/settings`)}
                className="cursor-pointer rounded-md p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600 dark:hover:bg-zinc-800 dark:hover:text-zinc-300"
                aria-label="Réglages du chantier"
                title="Réglages du chantier"
              >
                <GearSixIcon className="size-4" />
              </button>
            </div>
            {employeeName && (
              <p className="text-xs text-zinc-500">
                {employeeName}
                {isNotNil(functionLabel) && ` - ${functionLabel}`}
              </p>
            )}
          </div>
        </div>
      </div>

      {(yard.isLocked || isNil(yard.comissioningDate)) && (
        <div className="rounded bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
          {isNil(yard.comissioningDate)
            ? "Chantier en préparation"
            : "Chantier en maintenance"}
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleDownloadNomenclature}
          disabled={isDownloading}
          className="cursor-pointer rounded-md p-2 text-zinc-600 hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-50 dark:text-zinc-400 dark:hover:bg-zinc-800"
          aria-label="Télécharger la nomenclature"
          title="Télécharger la nomenclature"
        >
          <DownloadSimpleIcon className="size-5" />
        </button>

        <button
          type="button"
          onClick={onToggleActions}
          className="cursor-pointer rounded-md p-2 text-zinc-600 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800"
          aria-label="Ouvrir le panneau d'actions"
          title="Actions"
        >
          <LightningIcon className="size-5" />
        </button>

        <button
          type="button"
          onClick={() => navigate("/organizations/select")}
          className="cursor-pointer rounded-md p-2 text-zinc-600 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800"
          aria-label="Changer de chantier"
          title="Changer de chantier"
        >
          <ArrowsClockwiseIcon className="size-5" />
        </button>

        <button
          type="button"
          onClick={() => navigate("/auth/sign-out")}
          className="cursor-pointer rounded-md p-2 text-zinc-600 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800"
          aria-label="Déconnexion"
          title="Déconnexion"
        >
          <SignOutIcon className="size-5" />
        </button>
      </div>
    </header>
  );
}
